import { Users, Briefcase, UserPlus, Clock, TimerReset, CalendarCheck, CalendarClock, ListTree, Calculator, BarChart3, Gift, History, HeartPulse, Stamp, PiggyBank, Wallet, ShieldCheck, Banknote, FileText, Handshake, Scale, Settings, Building, BookOpen, UploadCloud, ShoppingCart } from 'lucide-react'
import { TabbedHub, type HubTab } from '@/components/TabbedHub'
import { EmployeesListPage } from './EmployeesListPage'
import { ContractorsListPage } from './ContractorsListPage'
import { PayComponentsListPage } from './PayComponentsListPage'
import { PaySchedulesSettingsPage } from './PaySchedulesSettingsPage'
import {
  OnboardingPage, TimesheetsPage, LeavePage, HolidaysPage, SchedulesPage,
  SalaryStructuresPage, PayGradesPage, EquityPage, ReviewCyclesPage,
  BenefitPlansPage, BenefitEnrollmentPage, RetirementPage, HsaFsaPage, AcaPage,
  NachaPage, ChecksPage, PaymentBatchesPage, GarnishmentsPage,
} from './WorkforceExtraPages'
import { BenefitsMarketplacePage } from './BenefitsMarketplacePage'
import {
  HealthcareCredentialsPage, PrevailingWagePage, DotDriverHoursPage,
  TipPoolsPage, AcademicContractsPage, UnionsPage, MigrationImporterPage,
} from './IndustryAndMigrationPages'

export function PeopleHub() {
  const tabs: HubTab[] = [
    { key: 'employees', label: 'Employees', icon: Users, permission: 'payroll.employees.view', element: <EmployeesListPage /> },
    { key: 'contractors', label: 'Contractors', icon: Briefcase, permission: 'payroll.contractors.view', element: <ContractorsListPage /> },
    { key: 'onboarding', label: 'Onboarding', icon: UserPlus, permission: 'payroll.employees.view', element: <OnboardingPage /> },
  ]
  return <TabbedHub tabs={tabs} />
}

export function TimeLeaveHub() {
  const tabs: HubTab[] = [
    { key: 'timesheets', label: 'Timesheets', icon: Clock, permission: 'payroll.time.view', element: <TimesheetsPage /> },
    { key: 'leave', label: 'Leave & PTO', icon: TimerReset, permission: 'payroll.leave.view', element: <LeavePage /> },
    { key: 'holidays', label: 'Holidays', icon: CalendarCheck, permission: 'payroll.holidays.manage', element: <HolidaysPage /> },
    { key: 'schedules', label: 'Schedules & Shifts', icon: CalendarClock, permission: 'payroll.schedules.manage', element: <SchedulesPage /> },
  ]
  return <TabbedHub tabs={tabs} />
}

export function CompensationHub() {
  const tabs: HubTab[] = [
    { key: 'components', label: 'Components', icon: Calculator, permission: 'payroll.components.view', element: <PayComponentsListPage /> },
    { key: 'structures', label: 'Salary Structures', icon: ListTree, permission: 'payroll.compensation.view', element: <SalaryStructuresPage />, smbHidden: true },
    { key: 'grades', label: 'Pay Grades', icon: BarChart3, permission: 'payroll.compensation.view', element: <PayGradesPage />, smbHidden: true },
    { key: 'equity', label: 'Equity Grants', icon: Gift, permission: 'payroll.equity.view', element: <EquityPage />, smbHidden: true },
    { key: 'cycles', label: 'Review Cycles', icon: History, permission: 'payroll.cycles.manage', element: <ReviewCyclesPage /> },
  ]
  return <TabbedHub tabs={tabs} />
}

export function BenefitsHub() {
  const tabs: HubTab[] = [
    { key: 'plans', label: 'Plans', icon: HeartPulse, permission: 'payroll.benefits.view', element: <BenefitPlansPage /> },
    { key: 'enrollment', label: 'Enrollment', icon: Stamp, permission: 'payroll.benefits.enroll', element: <BenefitEnrollmentPage /> },
    { key: 'retirement', label: '401(k) / Retirement', icon: PiggyBank, permission: 'payroll.retirement.manage', element: <RetirementPage /> },
    { key: 'hsa-fsa', label: 'HSA / FSA', icon: Wallet, permission: 'payroll.hsa_fsa.manage', element: <HsaFsaPage /> },
    { key: 'aca', label: 'ACA Tracking', icon: ShieldCheck, permission: 'payroll.aca.view', element: <AcaPage /> },
    { key: 'shop', label: 'Shop Plans', icon: ShoppingCart, permission: 'payroll.benefits.view', element: <BenefitsMarketplacePage /> },
  ]
  return <TabbedHub tabs={tabs} />
}

export function PaymentsHub() {
  const tabs: HubTab[] = [
    { key: 'nacha', label: 'Direct Deposit', icon: Banknote, permission: 'payroll.payments.view', element: <NachaPage /> },
    { key: 'checks', label: 'Checks', icon: FileText, permission: 'payroll.payments.checks', element: <ChecksPage /> },
    { key: 'batches', label: 'Payment Batches', icon: Handshake, permission: 'payroll.payments.batch', element: <PaymentBatchesPage /> },
    { key: 'garnishments', label: 'Garnishments', icon: Scale, permission: 'payroll.payments.view', element: <GarnishmentsPage /> },
  ]
  return <TabbedHub tabs={tabs} />
}

export function PayrollSettingsHub() {
  const tabs: HubTab[] = [
    { key: 'schedules', label: 'Pay Schedules', icon: Settings, permission: 'payroll.settings.view', element: <PaySchedulesSettingsPage /> },
    { key: 'healthcare', label: 'Healthcare', icon: HeartPulse, permission: 'payroll.industry.healthcare.manage', element: <HealthcareCredentialsPage />, smbHidden: true },
    { key: 'construction', label: 'Prevailing Wage', icon: Building, permission: 'payroll.industry.construction.manage', element: <PrevailingWagePage />, smbHidden: true },
    { key: 'transportation', label: 'DOT Hours', icon: ListTree, permission: 'payroll.industry.transportation.manage', element: <DotDriverHoursPage />, smbHidden: true },
    { key: 'hospitality', label: 'Tip Pools', icon: Wallet, permission: 'payroll.industry.hospitality.manage', element: <TipPoolsPage />, smbHidden: true },
    { key: 'education', label: 'Academic', icon: BookOpen, permission: 'payroll.industry.education.manage', element: <AcademicContractsPage />, smbHidden: true },
    { key: 'union', label: 'Unions & CBAs', icon: Users, permission: 'payroll.industry.union.manage', element: <UnionsPage />, smbHidden: true },
    { key: 'migration', label: 'Migration Importer', icon: UploadCloud, permission: 'payroll.migration.run', element: <MigrationImporterPage />, smbHidden: true },
  ]
  return <TabbedHub tabs={tabs} />
}
